/**
 * 1.3.0 Intake's list (/intake): one row per submission, with the chips (Waiting, Needs changes, Accepted in the last
 * 7 days, Withdrawn, All), filters (company, kind, overdue, search), keyset paging and the multi-select Accept for
 * open uploads that share a rule. P4's.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCheck, Film, Inbox, Search } from 'lucide-react';
import { INTAKE_CHIPS, type IntakeChip, type IntakeSubmissionDTO, type SubmissionKind } from '@prism/contracts';
import { errorMessage } from '../../api/client';
import { intakeApi, useIntakeAction, useIntakeList } from '../../api/hooks/intake';
import { QcBadge } from '../../components/QualityControl';
import { LoadMore } from '../../components/LoadMore';
import { EmptyState } from '../../components/EmptyState';
import { AssetCode } from '../../components/AssetCode';
import { toast } from '../../components/Toasts';
import { dateTime, relative } from '../../lib/format';
import { Due, ItemPill, SubmissionState } from './parts';
import { IntakeGroupFilter } from '../library/grouped/IntakeGroupFilter';

const CHIP_LABEL: Record<IntakeChip, string> = { waiting: 'Waiting', needs_changes: 'Needs changes', accepted: 'Accepted (7 days)', withdrawn: 'Withdrawn', all: 'All' };

export function IntakeList() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const chip = (INTAKE_CHIPS as readonly string[]).includes(params.get('chip') ?? '') ? params.get('chip') as IntakeChip : 'waiting';
  const kind = (params.get('kind') as SubmissionKind | null) ?? undefined;
  const companyId = params.get('company') ?? undefined;
  const overdue = params.get('overdue') === '1';
  const [search, setSearch] = useState(params.get('q') ?? '');
  const groupId = params.get('group') ?? undefined;
  const list = useIntakeList({ chip, kind, companyId, overdue, q: params.get('q') ?? undefined, groupId });
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) (v === null || v === '' ? next.delete(k) : next.set(k, v));
    setParams(next, { replace: true });
  };
  // Companies seen in the list (a reviewer needn't have portal.manage to filter by one).
  const [companies, setCompanies] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    const m = new Map(companies);
    let grew = false;
    for (const s of list.items) if (!m.has(s.company.id)) { m.set(s.company.id, s.company.name); grew = true; }
    if (grew) setCompanies(m);
  }, [list.items]); // eslint-disable-line react-hooks/exhaustive-deps

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const chosen = list.items.filter((s) => selected.has(s.id));
  const sameRule = chosen.length > 0 && chosen.every((s) => s.kind === 'open' && s.uploadRule && s.uploadRule.id === chosen[0]!.uploadRule?.id && s.state === 'waiting');
  const bulk = useIntakeAction((ids: string[]) => intakeApi.bulkAccept(ids));
  const acceptChosen = async () => {
    try {
      const r = await bulk.mutateAsync(chosen.map((s) => s.id));
      setSelected(new Set());
      toast(r.refused.length ? `${r.accepted.length} accepted, ${r.refused.length} not: ${r.refused[0]!.message}` : `${r.accepted.length} accepted into the Library`, r.refused.length ? 'error' : 'info');
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };
  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <>
      <header className="topbar">
        <h1><Inbox size={16} color="var(--accent-strong)" /> Intake</h1>
        <div className="intake-chips" role="group" aria-label="Show" data-testid="intake-chips">
          {INTAKE_CHIPS.map((c) => (
            <button key={c} className={`btn sm ${chip === c ? '' : 'ghost'}`} aria-pressed={chip === c} onClick={() => { setSelected(new Set()); set({ chip: c === 'waiting' ? null : c }); }} data-testid={`intake-chip-${c}`}>
              {CHIP_LABEL[c]}{list.counts && c !== 'all' ? <span className="intake-chip-count">{list.counts[c]}</span> : null}
            </button>
          ))}
        </div>
        <div className="topbar-spacer" />
        {chosen.length > 0 && (
          <button className="btn primary" disabled={!sameRule || bulk.isPending} onClick={() => void acceptChosen()} data-testid="intake-bulk-accept"
            title={sameRule ? 'Accept these open uploads with their rule’s destination' : 'Choose waiting open uploads of one rule'}>
            <CheckCheck size={15} /> Accept {chosen.length}
          </button>
        )}
      </header>
      <div className="intake-filters" data-testid="intake-filters">
        <form className="intake-search" onSubmit={(e) => { e.preventDefault(); set({ q: search }); }}>
          <Search size={14} className="faint" />
          <input className="input" placeholder="Search company, title, file…" value={search} onChange={(e) => setSearch(e.target.value)} onBlur={() => set({ q: search })} data-testid="intake-search" />
        </form>
        <select className="select" value={companyId ?? ''} onChange={(e) => set({ company: e.target.value })} data-testid="intake-filter-company" aria-label="Company">
          <option value="">All companies</option>
          {[...companies].sort((a, b) => a[1].localeCompare(b[1])).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <select className="select" value={kind ?? ''} onChange={(e) => set({ kind: e.target.value })} data-testid="intake-filter-kind" aria-label="Kind">
          <option value="">Deliveries and open uploads</option>
          <option value="delivery">Deliveries</option>
          <option value="open">Open uploads</option>
        </select>
        {/* 1.4.0 (G3): the Group filter. */}
        <IntakeGroupFilter value={groupId} onChange={(id) => set({ group: id })} />
        <label className="intake-check"><input type="checkbox" checked={overdue} onChange={(e) => set({ overdue: e.target.checked ? '1' : null })} data-testid="intake-filter-overdue" /> Overdue only</label>
      </div>
      <div className="page intake-page" data-testid="intake-page">
        {list.isLoading ? <div className="skeleton" style={{ height: 160 }} /> : list.items.length === 0 ? (
          <EmptyState icon={<Inbox size={28} />} title={chip === 'waiting' ? 'Nothing waiting for review' : 'No submissions here'} testId="intake-empty">
            Uploads from the media portal wait here until someone accepts them into the Library.
          </EmptyState>
        ) : (
          <div className="intake-table-wrap">
            <table className="table intake-table" data-testid="intake-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }} aria-label="Select" />
                  <th style={{ width: 72 }} aria-label="Preview" />
                  <th>Submission</th>
                  <th>Company</th>
                  <th>Items</th>
                  <th>QC</th>
                  <th>Arrived</th>
                  <th>Due</th>
                  <th>Reviewer</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((s) => <Row key={s.id} s={s} selected={selected.has(s.id)} onToggle={() => toggle(s.id)} onOpen={() => navigate(`/intake/${s.id}`)} />)}
              </tbody>
            </table>
          </div>
        )}
        <LoadMore list={list} shown={list.items.length} noun="submissions" />
      </div>
    </>
  );
}

function Row({ s, selected, onToggle, onOpen }: { s: IntakeSubmissionDTO; selected: boolean; onToggle: () => void; onOpen: () => void }) {
  const arrived = s.submittedAt ?? s.createdAt;
  return (
    <tr className={`intake-row ${selected ? 'selected' : ''}`} onClick={onOpen} data-testid="intake-row" data-id={s.id} data-kind={s.kind}>
      <td onClick={(e) => e.stopPropagation()}>
        {s.kind === 'open' && <input type="checkbox" checked={selected} onChange={onToggle} aria-label="Select" data-testid="intake-select" />}
      </td>
      <td>
        <span className="intake-thumb">{s.media?.thumbnailUrl ? <img src={s.media.thumbnailUrl} alt="" loading="lazy" /> : <Film size={16} className="faint" />}</span>
      </td>
      <td className="intake-title-cell">
        <Link to={`/intake/${s.id}`} onClick={(e) => e.stopPropagation()} className="intake-title">
          {s.assignment ? s.assignment.asset.title : s.media?.filename ?? 'Open upload'}
        </Link>
        <span className="faint intake-sub">
          {s.assignment ? <AssetCode code={s.assignment.asset.assetCode} /> : <span className="intake-open-tag">Open upload</span>}
          {s.media && s.assignment && <> · {s.media.filename}</>}
          {s.duplicateOf && <> · <span className="intake-dup" title={`Same file as ${s.duplicateOf.assetCode}`}>duplicate</span></>}
        </span>
      </td>
      <td className="intake-company">
        <span>{s.company.name}</span>
        {s.portalUser && <span className="faint intake-sub">{s.portalUser.name}</span>}
      </td>
      <td className="intake-items-cell">
        {s.items.length ? <span className="intake-pills">{s.items.map((i) => <ItemPill key={i.id} item={i} />)}</span> : <span className="faint">Media only</span>}
      </td>
      <td>{s.media?.qcStatus ? <QcBadge status={s.media.qcStatus as never} compact /> : <span className="faint">—</span>}</td>
      <td className="muted" title={dateTime(arrived)}>{relative(arrived)}</td>
      <td><Due dueAt={s.assignment?.dueAt ?? null} overdue={s.overdue} /></td>
      <td className="muted">{s.reviewer?.name ?? 'Anyone'}</td>
      <td><SubmissionState state={s.state} /></td>
    </tr>
  );
}
